import {
  calculateAwaitingEndLate,
  calculateEndedLate,
  calculateOnlyEndLate,
  calculateNotStartedAndStartPassed,
  calculateNotStartedAndEndPassed,
  calculateStartedLate,
} from "@pages/Dashboard/utils/eventLateCalculations";
import { useMemo } from "react";

/**
 * Flags de atraso de um evento na timeline
 * 
 * @interface EventLateFlags
 * @property {boolean} notStartedAndStartPassed - Processo não iniciou e passou do horário de início previsto. Usado para pintar START.
 * @property {boolean} notStartedAndEndPassed - Processo não iniciou e passou do horário de fim previsto. Usado para pintar END.
 * @property {boolean} startedLate - Processo iniciou atrasado (executionStart > plannedExecutionStart). Usado para pintar START.
 * @property {boolean} onlyEndLate - Processo iniciou mas não finalizou e passou do horário de fim previsto. Usado para pintar END.
 * @property {boolean} endedLate - Processo finalizou atrasado (executionEnd > plannedExecutionEnd). Usado para pintar END.
 * @property {boolean} awaitingEndLate - Processo AWAITING_END (OneShot) está atrasado.
 */
export interface EventLateFlags {
  notStartedAndStartPassed: boolean;
  notStartedAndEndPassed: boolean;
  startedLate: boolean;
  onlyEndLate: boolean;
  endedLate: boolean;
  awaitingEndLate: boolean;
}

/**
 * Função pura para calcular todos os flags de atraso de um evento
 * Pode ser usada em funções puras (não hooks)
 */
export function calculateEventLateFlags(event?: Sentinel.Timeline.Event): EventLateFlags {
  if (!event) {
    return {
      notStartedAndStartPassed: false,
      notStartedAndEndPassed: false,
      startedLate: false,
      onlyEndLate: false,
      endedLate: false,
      awaitingEndLate: false,
    };
  }

  const status = event.status;
  const isNotExecuted = status === "CANCELED";

  // CANCELED: não está atrasado
  if (isNotExecuted) {
    return {
      notStartedAndStartPassed: false,
      notStartedAndEndPassed: false,
      startedLate: false,
      onlyEndLate: false,
      endedLate: false,
      awaitingEndLate: false,
    };
  }

  // AWAITING_END: lógica específica oneShot
  if (status === "AWAITING_END") {
    let awaitingEndLate: boolean;
    if (event.executionEnd) {
      awaitingEndLate = calculateEndedLate(event.executionEnd, event.plannedExecutionEnd);
    } else {
      awaitingEndLate = calculateAwaitingEndLate(event.executionEnd, event.plannedExecutionEnd);
    }

    return {
      notStartedAndStartPassed: false,
      notStartedAndEndPassed: false,
      startedLate: false,
      onlyEndLate: false,
      endedLate: false,
      awaitingEndLate,
    };
  }

  // Status que já indicam atraso no início
  if (status === "STARTED_LATE" || status === "DELAYED") {
    return {
      notStartedAndStartPassed: calculateNotStartedAndStartPassed(
        event.executionStart,
        event.plannedExecutionStart
      ),
      notStartedAndEndPassed: calculateNotStartedAndEndPassed(
        event.executionStart,
        event.executionEnd,
        event.plannedExecutionEnd
      ),
      startedLate: true,
      onlyEndLate: calculateOnlyEndLate(
        event.executionStart,
        event.executionEnd,
        event.plannedExecutionEnd
      ),
      endedLate: calculateEndedLate(
        event.executionEnd,
        event.plannedExecutionEnd
      ),
      awaitingEndLate: false,
    };
  }

  // Status que já indica atraso no fim
  if (status === "COMPLETED_LATE") {
    return {
      notStartedAndStartPassed: calculateNotStartedAndStartPassed(
        event.executionStart,
        event.plannedExecutionStart
      ),
      notStartedAndEndPassed: calculateNotStartedAndEndPassed(
        event.executionStart,
        event.executionEnd,
        event.plannedExecutionEnd
      ),
      startedLate: calculateStartedLate(
        event.executionStart,
        event.plannedExecutionStart
      ),
      onlyEndLate: false,
      endedLate: true,
      awaitingEndLate: false,
    };
  }

  // Status que indica não atrasado no início
  if (status === "STARTED_EARLY") {
    return {
      notStartedAndStartPassed: false,
      notStartedAndEndPassed: calculateNotStartedAndEndPassed(
        event.executionStart,
        event.executionEnd,
        event.plannedExecutionEnd
      ),
      startedLate: false,
      onlyEndLate: calculateOnlyEndLate(
        event.executionStart,
        event.executionEnd,
        event.plannedExecutionEnd
      ),
      endedLate: calculateEndedLate(
        event.executionEnd,
        event.plannedExecutionEnd
      ),
      awaitingEndLate: false,
    };
  }

  // COMPLETED_EARLY: finalizou antes, mas pode não ter iniciado (executionStart pode ser null)
  if (status === "COMPLETED_EARLY") {
    return {
      notStartedAndStartPassed: calculateNotStartedAndStartPassed(
        event.executionStart,
        event.plannedExecutionStart
      ),
      notStartedAndEndPassed: false,
      startedLate: calculateStartedLate(
        event.executionStart,
        event.plannedExecutionStart
      ),
      onlyEndLate: false,
      endedLate: false,
      awaitingEndLate: false,
    };
  }

  // Outros status: calcular todos os flags
  return {
    notStartedAndStartPassed: calculateNotStartedAndStartPassed(
      event.executionStart,
      event.plannedExecutionStart
    ),
    notStartedAndEndPassed: calculateNotStartedAndEndPassed(
      event.executionStart,
      event.executionEnd,
      event.plannedExecutionEnd
    ),
    startedLate: calculateStartedLate(
      event.executionStart,
      event.plannedExecutionStart
    ),
    onlyEndLate: calculateOnlyEndLate(
      event.executionStart,
      event.executionEnd,
      event.plannedExecutionEnd
    ),
    endedLate: calculateEndedLate(
      event.executionEnd,
      event.plannedExecutionEnd
    ),
    awaitingEndLate: false,
  };
}

/**
 * Hook centralizado para calcular todos os flags de atraso de um evento
 * Usa a função pura calculateEventLateFlags internamente
 */
export function useEventLateFlags(event?: Sentinel.Timeline.Event): EventLateFlags {
  return useMemo(() => calculateEventLateFlags(event), [event]);
}
